import type { AppContext } from '@application/context/AppContext';
import { isHybridDeployment, isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { BusinessApiError, getBusinessApiClient } from '@infra/api/BusinessApiClient';
import type { RemoteUserRecord } from '@infra/api/BusinessApiClient';
import type { Role, User } from '@shared/types/User';
import { compterAdminsActifs, motifRefusCompte } from './UserAccountGuard';
import { assertCanWriteEntity } from '@infra/tenancy/entityWriteGuard';

/** Offre Hybride : les comptes sont ceux du serveur (le poste n'en garde qu'un cache d'identité). */
function isHybridAccounts(ctx: AppContext): boolean {
  return (
    !isRemoteMultiStationMode(ctx.getLockedDataMode()) &&
    isHybridDeployment(ctx.getLicenceRuntime?.()?.getState()?.claims ?? null)
  );
}

const HYBRID_ONLINE_REQUIRED =
  'Connexion à Internet requise : dans l’offre Hybride, les comptes sont gérés sur le serveur.';

/** Les comptes se créent et se modifient sur le serveur ; hors ligne, l'action est refusée clairement. */
async function onServer<T>(action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (err) {
    if (err instanceof BusinessApiError && err.statusCode !== undefined) throw err;
    throw new Error(HYBRID_ONLINE_REQUIRED);
  }
}

function remoteToUser(remote: RemoteUserRecord): User {
  const parts = remote.display_name.trim().split(/\s+/).filter(Boolean);
  const prenom = parts[0] ?? '';
  const nom = parts.slice(1).join(' ') || prenom;
  return {
    id: remote.id,
    tenant_id: remote.tenant_id,
    email: remote.email,
    nom,
    prenom,
    role: remote.role as Role,
    salarie_id: remote.salarie_id ?? undefined,
    password_hash: '',
    password_salt: '',
    actif: remote.active ?? true,
    cree_le: remote.created_at ?? new Date().toISOString(),
  };
}

/** Liste les comptes depuis la source adaptée au mode de données. */
export async function listTenantUsers(
  ctx: AppContext,
  tenantId: string | undefined,
): Promise<User[]> {
  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    const items = await getBusinessApiClient().listUsers();
    return items.map(remoteToUser);
  }
  if (isHybridAccounts(ctx)) {
    try {
      const items = await getBusinessApiClient().listUsers();
      return items.map(remoteToUser);
    } catch {
      // Hors ligne : cache d'identité des comptes déjà connus de ce poste.
      return (await ctx.localTables.list<User>('users', tenantId ?? undefined)).filter((u) => Boolean(u.identity_verified_at));
    }
  }
  return ctx.localTables.list<User>('users', tenantId ?? undefined);
}

/** Crée un compte sur la source adaptée au mode de données. */
export async function createTenantUser(
  ctx: AppContext,
  tenantId: string,
  input: {
    email: string;
    password: string;
    nom: string;
    prenom: string;
    role: Role;
  },
): Promise<User> {
  assertCanWriteEntity('users', 'create');
  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    const created = await getBusinessApiClient().createUser(input);
    return remoteToUser(created);
  }
  if (isHybridAccounts(ctx)) {
    const created = await onServer(() => getBusinessApiClient().createUser(input));
    return remoteToUser(created);
  }
  const { user } = await ctx.auth.register(
    tenantId,
    input.email,
    input.password,
    input.nom,
    input.prenom,
    input.role,
  );
  return user;
}

export async function resetTenantUserPassword(
  ctx: AppContext,
  userId: string,
  password: string,
): Promise<void> {
  assertCanWriteEntity('users', 'update');
  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    await getBusinessApiClient().resetUserPassword(userId, password);
    return;
  }
  if (isHybridAccounts(ctx)) {
    await onServer(() => getBusinessApiClient().resetUserPassword(userId, password));
    return;
  }
  await ctx.auth.resetPassword(userId, password);
}

/** Résout un compte par id (session courante ou annuaire tenant). */
export async function getTenantUserById(
  ctx: AppContext,
  userId: string,
  tenantId: string | undefined,
): Promise<User | undefined> {
  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    const me = await getBusinessApiClient().fetchMe();
    if (me?.id === userId) {
      return remoteToUser({ ...me, active: true });
    }
    const users = await listTenantUsers(ctx, tenantId);
    return users.find((u) => u.id === userId);
  }
  return (await ctx.localTables.get<User>('users', userId)) ?? undefined;
}

/** Met à jour le rôle et/ou l'activation d'un compte (désactivation logique). */
export async function updateTenantUser(
  ctx: AppContext,
  userId: string,
  patch: { role?: Role; actif?: boolean },
): Promise<User> {
  const session = await ctx.auth.requireRoleFresh('admin', 'super_admin');
  const tenantId = session.tenant_id;
  const users = await listTenantUsers(ctx, tenantId);
  const target = users.find((u) => u.id === userId);
  if (!target) throw new Error('Utilisateur introuvable');

  const motif = motifRefusCompte({
    actorId: session.user_id,
    targetId: target.id,
    targetRole: target.role,
    targetActive: target.actif,
    nextRole: patch.role,
    nextActive: patch.actif,
    activeAdminCount: compterAdminsActifs(users),
  });
  if (motif) throw new Error(motif);

  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    const updated = await getBusinessApiClient().updateUser(userId, {
      ...(patch.role ? { role: patch.role } : {}),
      ...(patch.actif !== undefined ? { active: patch.actif } : {}),
    });
    return remoteToUser(updated);
  }
  if (isHybridAccounts(ctx)) {
    const updated = await onServer(() =>
      getBusinessApiClient().updateUser(userId, {
        ...(patch.role ? { role: patch.role } : {}),
        ...(patch.actif !== undefined ? { active: patch.actif } : {}),
      }),
    );
    const cached = await ctx.localTables.get<User>('users', userId);
    if (cached) {
      await ctx.localTables.put('users', {
        ...cached,
        ...(patch.role ? { role: patch.role } : {}),
        ...(patch.actif !== undefined ? { actif: patch.actif } : {}),
      });
    }
    return remoteToUser(updated);
  }

  const stored = (await ctx.userRepo.findById(userId)) ?? target;
  const next: User = {
    ...stored,
    ...(patch.role ? { role: patch.role } : {}),
    ...(patch.actif !== undefined ? { actif: patch.actif } : {}),
  };
  await ctx.userRepo.update(next);
  return next;
}
